import { z } from 'zod';

// Referral backfill (T-110). Input = a referrer vendor's cumulative master
// list (tab 2 of Eric's per-vendor files) as a single-column CSV of
// "Last, First" names + the vendor chosen in the UI. A matched name sets
// engagements.referred_by_vendor_id on every engagement of that resource that
// has no referrer yet; an existing different referrer is reported as a
// conflict, never overwritten — so re-runs are idempotent.
export const ReferralBackfillRowStatus = z.enum([
  'matched',
  'already_set',
  'conflict',
  'ambiguous',
  'unmatched',
  'no_engagements',
]);
export type ReferralBackfillRowStatus = z.infer<typeof ReferralBackfillRowStatus>;

export const ReferralBackfillRow = z.object({
  // Name as uploaded (first occurrence), trimmed for display.
  name: z.string(),
  // 1-based CSV row numbers; duplicates fold into one entry with 2+ rows.
  row_numbers: z.array(z.number().int().positive()),
  status: ReferralBackfillRowStatus,
  // matched: how many engagements the import would set (0 for other statuses).
  engagements_to_set: z.number().int().nonnegative(),
  // conflict: the other referrers already on this resource's engagements.
  conflict_vendors: z.array(z.string()),
  // ambiguous: how many same-name resources matched (0 / 1 otherwise).
  resource_matches: z.number().int().nonnegative(),
});
export type ReferralBackfillRow = z.infer<typeof ReferralBackfillRow>;

export const ReferralBackfillPreviewResponse = z.object({
  vendor: z.object({ id: z.string().uuid(), business_name: z.string() }),
  rows_in: z.number().int().nonnegative(),
  unique_names: z.number().int().nonnegative(),
  matched: z.number().int().nonnegative(),
  engagements_to_set: z.number().int().nonnegative(),
  already_set: z.number().int().nonnegative(),
  conflicts: z.number().int().nonnegative(),
  ambiguous: z.number().int().nonnegative(),
  unmatched: z.number().int().nonnegative(),
  no_engagements: z.number().int().nonnegative(),
  rows: z.array(ReferralBackfillRow),
});
export type ReferralBackfillPreviewResponse = z.infer<typeof ReferralBackfillPreviewResponse>;

export const ReferralBackfillImportResponse = z.object({
  engagements_set: z.number().int().nonnegative(),
  resources_affected: z.number().int().nonnegative(),
  rows_skipped: z.number().int().nonnegative(),
});
export type ReferralBackfillImportResponse = z.infer<typeof ReferralBackfillImportResponse>;
